import {useEffect, useState} from 'react';
import {WidgetName} from '@src/utils/enums';
import type {BaseWidgetProps, DirectionalWidgetProps} from './widgets/types';
import {useStores} from '@src/components/store';

export type UseReportWidgetDataProps =
    (BaseWidgetProps | DirectionalWidgetProps) & { reportName: string };

interface UseReportWidgetDataResult<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

const widgetNames: {[index:string]: WidgetName} = {
  // Trend cards
  'blockedSessions': WidgetName.BLOCKED_SESSIONS_WIDGET,
  'allowedSessions': WidgetName.ALLOWED_SESSIONS_WIDGET,
  'criticalThreatsBlocked': WidgetName.CRITICAL_THREATS_WIDGET,
  // Blocked threats by severity
  'blockedThreatsBySeverity': WidgetName.BLOCKED_THREATS_BY_SEVERITY_WIDGET,
  // Blocked sessions by type
  'blockedSessionsByType': WidgetName.BLOCKED_SESSIONS_BY_TYPE_WIDGET,
  // Network traffic
  'networkTraffic': WidgetName.TOP_USED_APPS_WIDGET,
  // AI Network traffic
  'aiNetworkTraffic': WidgetName.TOP_AI_USED_APPS_WIDGET,
  // Top blocked domains
  'topBlockedDomains': WidgetName.TOP_BLOCKED_DOMAINS_WIDGET,
  // Top countries traffic
  'topCountriesBlockedTraffic': WidgetName.BLOCKED_COUNTRIES_WIDGET,
  'topCountriesAllowedTraffic': WidgetName.ALLOWED_COUNTRIES_WIDGET,
};

export function useReportWidgetData<T = any>(props: UseReportWidgetDataProps): UseReportWidgetDataResult<T> {
  const { fromDate, toDate, reportName, customerId, serviceId } = props;
  const { reportsStore } = useStores();

  // direction may or may not exist on props
  const direction = (props as any).direction;
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await reportsStore.loadWidgetData(widgetNames[reportName], direction);
      setData(result);
    } catch (e) {
      console.error('Failed to fetch report widget data: ', e);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchData();
  }, [fromDate, toDate, reportName, direction, customerId, serviceId]);

  return { data, loading, error };
}
